'use client';

import { useEffect, useState } from 'react';
import { Eye, Inbox, Share2 } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import HeroVista from '@/components/HeroVista';
import {
  listarExpedientes, asignarReparto, funcionariosInstruccion,
  type Expediente, type UsuarioLite,
} from '@/lib/expedientes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

// Nombre del investigado "principal" (el primero determinado, o el primero).
function investigadoPrincipal(e: Expediente): string {
  const inv = e.investigados ?? [];
  if (inv.length === 0) return '—';
  const det = inv.find((x) => x.tipo === 'DETERMINADO' && x.nombre) ?? inv[0];
  return det.nombre || (det.tipo === 'POR_ESTABLECER' ? 'Individualizar' : '—');
}

export default function BandejaReparto({ onAbrir }: { onAbrir?: (id: string) => void }) {
  const [expedientes, setExpedientes] = useState<Expediente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [asignar, setAsignar] = useState<Expediente | null>(null);

  async function cargar() {
    setCargando(true);
    try {
      const res = await listarExpedientes({ estado: 'PENDIENTE_REPARTO', limit: 100 });
      setExpedientes(res.data);
    } catch (e) {
      avisoError('No se pudo cargar la bandeja de reparto', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  return (
    <div className="space-y-6">
      <HeroVista
        icono={Inbox}
        titulo="Reparto"
        descripcion="Expedientes pendientes de asignación. Solo el Director reparte los expedientes a los funcionarios de instrucción."
      />

      <Card>
        <CardContent className="pt-6">
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Radicado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo ingreso</TableHead>
                  <TableHead>Investigado principal</TableHead>
                  <TableHead>Prescripción</TableHead>
                  <TableHead>Prioridad</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expedientes.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">
                      {e.radicado}
                      {e.fecha_hechos_tentativa && (
                        <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-700 dark:text-amber-400" title="Fecha de los hechos tentativa — falta la real">Fecha tentativa</Badge>
                      )}
                    </TableCell>
                    <TableCell>{fmtFecha(e.fecha_queja)}</TableCell>
                    <TableCell>{e.tipo_ingreso}</TableCell>
                    <TableCell>{investigadoPrincipal(e)}</TableCell>
                    <TableCell>{fmtFecha(e.fecha_prescripcion)}</TableCell>
                    <TableCell>
                      {e.prioridad ? <Badge variant="secondary">{e.prioridad}</Badge> : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => onAbrir?.(e.id)}>
                          <Eye className="size-3.5" /> Ver
                        </Button>
                        <Button size="sm" onClick={() => setAsignar(e)}>
                          <Share2 className="size-3.5" /> Asignar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {expedientes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      No hay expedientes pendientes de reparto.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {asignar && (
        <ModalAsignar
          expediente={asignar}
          onClose={() => setAsignar(null)}
          onListo={(msg) => { setAsignar(null); avisoExito(msg); cargar(); }}
        />
      )}
    </div>
  );
}

// ---- Modal: asignar reparto -----------------------------------------------
function ModalAsignar({ expediente, onClose, onListo }: {
  expediente: Expediente; onClose: () => void; onListo: (msg: string) => void;
}) {
  const [funcionarios, setFuncionarios] = useState<UsuarioLite[]>([]);
  const [funcionarioId, setFuncionarioId] = useState('');
  const [termino, setTermino] = useState('');
  const [prioridad, setPrioridad] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    funcionariosInstruccion()
      .then(setFuncionarios)
      .catch((e) =>
        avisoError('No se pudieron cargar los funcionarios', e instanceof Error ? e.message : undefined),
      );
  }, []);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!funcionarioId) { avisoError('Seleccione un funcionario.'); return; }
    setGuardando(true);
    try {
      const body: { funcionarioId: string; terminoInterno?: string; prioridad?: string; observaciones?: string } = { funcionarioId };
      if (termino.trim()) body.terminoInterno = termino.trim();
      if (prioridad.trim()) body.prioridad = prioridad.trim();
      if (observaciones.trim()) body.observaciones = observaciones.trim();
      await asignarReparto(expediente.id, body);
      onListo(`Expediente ${expediente.radicado} asignado correctamente.`);
    } catch (err) {
      avisoError('No se pudo asignar el reparto', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="size-5 text-primary" /> Asignar reparto
          </DialogTitle>
          <DialogDescription>
            Radicado: <strong>{expediente.radicado}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label>Funcionario</Label>
            <Select value={funcionarioId} onValueChange={setFuncionarioId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
              <SelectContent>
                {funcionarios.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.nombre} {f.apellido} · {f.rol}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="termino">Término interno (opcional)</Label>
              <Input id="termino" value={termino} onChange={(e) => setTermino(e.target.value)} placeholder="Ej: 30 días" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prioridad">Prioridad (opcional)</Label>
              <Input id="prioridad" value={prioridad} onChange={(e) => setPrioridad(e.target.value)} placeholder="Ej: ALTA" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="observaciones">Observaciones (opcional)</Label>
            <Textarea id="observaciones" rows={3} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>
              {guardando ? 'Asignando…' : 'Asignar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
